import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { reportsAPI, filesAPI, projectsAPI } from '../../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  ArrowLeft,
  FileText,
  MapPin,
  Calendar,
  User,
  CheckCircle2,
  AlertTriangle,
  Send,
  Download,
  Eye,
  Loader2,
  MessageSquare,
  ThumbsUp,
  RotateCcw,
  Lightbulb,
  ClipboardList,
  Target,
  Users,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const statusConfig = {
  released: { label: 'Released', color: 'bg-blue-100 text-blue-700', icon: Eye },
  donor_approved: { label: 'Accepted by You', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  donor_revision_requested: { label: 'Revision Requested', color: 'bg-amber-100 text-amber-700', icon: RotateCcw },
};

function getSummaryInsight(indicators) {
  if (!indicators || indicators.length === 0) return null;
  const levels = indicators.map(i => i.discrepancy_level).filter(l => l && l !== 'none');
  if (levels.length === 0) return null;
  const hasMismatch = levels.includes('mismatch');
  if (hasMismatch) return { text: 'Mismatch detected — some indicator values differ significantly between verifiers', color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: AlertTriangle };
  return { text: 'Verification results are consistent across both independent verifiers', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 };
}

export default function DonorReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const res = await reportsAPI.get(reportId);
      setReport(res.data);
      if (res.data.donor_feedback) setFeedbackText(res.data.donor_feedback);

      // Fetch comparison data for the project
      try {
        const compRes = await projectsAPI.getIndicatorComparison(res.data.project_id);
        setComparison(compRes.data);
      } catch {}
    } catch (error) {
      toast.error('Failed to load report');
      navigate('/donor');
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (action) => {
    if (action === 'request_revision' && !feedbackText.trim()) {
      toast.error('Please provide feedback comments when requesting a revision');
      return;
    }
    setSubmitting(true);
    try {
      await reportsAPI.donorFeedback(reportId, action, feedbackText);
      toast.success(action === 'approve' ? 'Report accepted!' : 'Revision request sent');
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/export/project/${report.project_id}`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!report) return null;

  const status = statusConfig[report.status] || { label: report.status, color: 'bg-slate-100 text-slate-700', icon: FileText };
  const StatusIcon = status.icon;
  const canReview = report.status === 'released';
  const hasReviewed = ['donor_approved', 'donor_revision_requested'].includes(report.status);
  const isDouble = comparison?.verification_mode === 'double';
  const insight = isDouble ? getSummaryInsight(comparison?.indicators) : null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="donor-report-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/donor')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{report.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-slate-500">{report.project_title}</p>
              {isDouble && <Badge className="bg-indigo-100 text-indigo-700 gap-1 text-xs"><Users className="w-3 h-3" />Double Verified</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${status.color} gap-1`}><StatusIcon className="w-3 h-3" />{status.label}</Badge>
          <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Report Meta */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-slate-400" />
              <div><p className="text-slate-500 text-xs">Field Agent</p><p className="font-medium text-slate-900">{report.agent_name}</p></div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div><p className="text-slate-500 text-xs">Submitted</p><p className="font-medium text-slate-900">{report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : 'N/A'}</p></div>
            </div>
            {report.activity_type && (
              <div className="flex items-center gap-2 text-sm">
                <ClipboardList className="w-4 h-4 text-slate-400" />
                <div><p className="text-slate-500 text-xs">Activity Type</p><p className="font-medium text-slate-900">{report.activity_type}</p></div>
              </div>
            )}
            {(report.latitude && report.longitude) && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-slate-400" />
                <div><p className="text-slate-500 text-xs">Location</p><p className="font-medium text-slate-900">{report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}</p></div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><FileText className="w-5 h-5 text-slate-600" />Report Content</CardTitle></CardHeader>
        <CardContent><p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{report.content}</p></CardContent>
      </Card>

      {report.findings && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Lightbulb className="w-5 h-5 text-amber-500" />Key Findings</CardTitle></CardHeader>
          <CardContent><p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{report.findings}</p></CardContent>
        </Card>
      )}

      {report.recommendations && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><CheckCircle2 className="w-5 h-5 text-emerald-500" />Recommendations</CardTitle></CardHeader>
          <CardContent><p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{report.recommendations}</p></CardContent>
        </Card>
      )}

      {/* Evidence Files */}
      {report.evidence_file_ids?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Download className="w-5 h-5 text-slate-600" />Evidence Files ({report.evidence_file_ids.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {report.evidence_file_ids.map((fileId) => (
                <a key={fileId} href={filesAPI.view(fileId)} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-sm" data-testid={`evidence-file-${fileId}`}>
                  <FileText className="w-4 h-4 text-slate-500" /><span className="text-slate-700 truncate">{fileId}</span><Eye className="w-4 h-4 text-slate-400 ml-auto" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Double Verification Comparison Section */}
      {isDouble && comparison?.indicators?.length > 0 && (
        <Card data-testid="donor-double-comparison">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Target className="w-5 h-5 text-indigo-500" />Verification Comparison Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Insight */}
            {insight && (
              <div className={`flex items-start gap-3 p-4 rounded-lg border ${insight.bg}`} data-testid="verification-insight">
                <insight.icon className={`w-5 h-5 mt-0.5 ${insight.color}`} />
                <p className={`text-sm font-medium ${insight.color}`}>{insight.text}</p>
              </div>
            )}

            {/* Comparison Table */}
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="donor-comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 font-medium text-slate-700">Indicator</th>
                    <th className="text-left p-3 font-medium text-slate-700">Planned Value</th>
                    <th className="text-left p-3 font-medium text-slate-700">Verifier A</th>
                    <th className="text-left p-3 font-medium text-slate-700">Verifier B</th>
                    <th className="text-left p-3 font-medium text-slate-700">Final Verified Value</th>
                    <th className="text-left p-3 font-medium text-slate-700">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.indicators.map((ci, idx) => {
                    const final = comparison.final_verified_values?.find(f => f.indicator_name === ci.indicator_name);
                    const isMismatch = ci.discrepancy_level === 'mismatch';
                    return (
                      <tr key={idx} className={`border-b border-slate-100 last:border-0 ${isMismatch ? 'bg-red-50/50' : ''}`}>
                        <td className="p-3 font-medium text-slate-900">{ci.indicator_name}</td>
                        <td className="p-3 text-slate-600">{ci.donor_value || '-'}</td>
                        <td className="p-3 text-slate-700">{ci.agent1_value || <span className="text-slate-400 italic">-</span>}</td>
                        <td className="p-3 text-slate-700">{ci.agent2_value || <span className="text-slate-400 italic">-</span>}</td>
                        <td className="p-3 font-semibold text-indigo-700">{final?.final_value || ci.final_value || <span className="text-slate-400">-</span>}</td>
                        <td className="p-3">
                          {ci.discrepancy_level && ci.discrepancy_level !== 'none' ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                              isMismatch ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {isMismatch ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                              {isMismatch ? 'Mismatch' : 'Consistent'}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Grouped Bar Chart */}
            {(() => {
              const chartData = comparison.indicators
                .filter(ci => {
                  const dv = parseFloat(ci.donor_value);
                  return !isNaN(dv) && (ci.agent1_value || ci.agent2_value);
                })
                .map(ci => {
                  const final = comparison.final_verified_values?.find(f => f.indicator_name === ci.indicator_name);
                  return {
                    name: ci.indicator_name.length > 18 ? ci.indicator_name.substring(0, 16) + '...' : ci.indicator_name,
                    'Planned Value': parseFloat(ci.donor_value) || 0,
                    'Verifier A': parseFloat(ci.agent1_value) || 0,
                    'Verifier B': parseFloat(ci.agent2_value) || 0,
                    'Final Value': parseFloat(final?.final_value || ci.final_value) || 0,
                  };
                });
              if (chartData.length === 0) return null;
              return (
                <div data-testid="donor-double-chart">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Verification Comparison Chart</h4>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} barGap={2} barCategoryGap="15%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="Planned Value" fill="#6366F1" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Verifier A" fill="#10B981" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Verifier B" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Final Value" fill="#0F172A" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Single Verification Indicator Comparison (existing) */}
      {!isDouble && report.indicator_verification?.length > 0 && (
        <Card data-testid="donor-indicator-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Target className="w-5 h-5 text-indigo-500" />Indicator Verification Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="indicator-comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 font-medium text-slate-700">Indicator</th>
                    <th className="text-left p-3 font-medium text-slate-700">Your Target</th>
                    <th className="text-left p-3 font-medium text-slate-700">Verified Value</th>
                    <th className="text-left p-3 font-medium text-slate-700">Status</th>
                    <th className="text-left p-3 font-medium text-slate-700">Agent Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {report.indicator_verification.map((iv, idx) => {
                    const targetNum = parseFloat(iv.donor_value);
                    const verifiedNum = parseFloat(iv.verified_value);
                    const hasValues = !isNaN(targetNum) && !isNaN(verifiedNum);
                    const match = hasValues && verifiedNum >= targetNum;
                    return (
                      <tr key={idx} className="border-b border-slate-100 last:border-0">
                        <td className="p-3 font-medium text-slate-900">{iv.indicator_name}</td>
                        <td className="p-3 text-slate-600">{iv.donor_value || '-'}</td>
                        <td className="p-3 font-medium">
                          {iv.verified_value ? (
                            <span className={hasValues ? (match ? 'text-emerald-600' : 'text-amber-600') : 'text-slate-700'}>{iv.verified_value}</span>
                          ) : <span className="text-slate-400 italic">Pending</span>}
                        </td>
                        <td className="p-3">
                          {iv.verified_value ? (hasValues ? (match ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Met</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Below Target</span>
                          )) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded-full"><Eye className="w-3 h-3" /> Verified</span>
                          )) : <span className="text-slate-400 text-xs">-</span>}
                        </td>
                        <td className="p-3 text-slate-500 text-xs max-w-[200px] truncate">{iv.agent_notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Single verification chart */}
            {(() => {
              const chartData = report.indicator_verification
                .filter(iv => iv.verified_value && !isNaN(parseFloat(iv.donor_value)) && !isNaN(parseFloat(iv.verified_value)))
                .map(iv => ({
                  name: iv.indicator_name.length > 20 ? iv.indicator_name.substring(0, 18) + '...' : iv.indicator_name,
                  'Donor Target': parseFloat(iv.donor_value),
                  'Verified Value': parseFloat(iv.verified_value),
                }));
              if (chartData.length === 0) return null;
              return (
                <div data-testid="indicator-comparison-chart">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Target vs Verified Comparison</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} barGap={4} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                      <Legend wrapperStyle={{ fontSize: '13px' }} />
                      <Bar dataKey="Donor Target" fill="#6366F1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Verified Value" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Previous Donor Feedback */}
      {hasReviewed && report.donor_feedback && (
        <Card className={report.status === 'donor_approved' ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {report.status === 'donor_approved' ? <ThumbsUp className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
              <span className={report.status === 'donor_approved' ? 'text-emerald-800' : 'text-amber-800'}>Your Feedback</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`leading-relaxed ${report.status === 'donor_approved' ? 'text-emerald-700' : 'text-amber-700'}`}>{report.donor_feedback}</p>
            <p className="text-xs text-slate-400 mt-3">Submitted on {report.donor_reviewed_at ? new Date(report.donor_reviewed_at).toLocaleString() : 'N/A'}</p>
          </CardContent>
        </Card>
      )}

      {/* Feedback Form */}
      {canReview && (
        <Card className="border-slate-300 shadow-md" data-testid="donor-feedback-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Send className="w-5 h-5 text-slate-600" />Your Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Comments / Feedback</label>
              <Textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Share your feedback on this report..." rows={4} className="resize-none" data-testid="donor-feedback-textarea" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => handleFeedback('approve')} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="donor-approve-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-2" />}Accept Report
              </Button>
              <Button onClick={() => handleFeedback('request_revision')} disabled={submitting} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 flex-1" data-testid="donor-request-revision-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}Request Revision
              </Button>
            </div>
            <p className="text-xs text-slate-400">Accepting means you're satisfied with the verification. Requesting revision will notify the admin and field agent.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
